import { useAudits } from '@api/audits';
import { DataGrid } from '@components/DataGrid';
import { styled } from '@mui/system';
import { GridFilterModel, GridRowParams } from '@mui/x-data-grid';
import { maxAuditsSearch, DefaultExcludedActions } from '@utils/audits';
import { getAuditsColumns } from '@utils/tables';
import { IS_TEST_MODE } from 'constants/global';
import React, { useCallback, useState } from 'react';
import { AuditsTimePeriod, IAudit } from 'types/audits';

import { useDebounce } from 'usehooks-ts';
import AuditDrawer from '../Drawer';
import AuditSelectTimePeriod from './AuditSelectTimePeriod';

const AuditsDataGrid = styled(DataGrid)`
	.audits-table-row {
		cursor: pointer;
	}
`;

const AuditsTable: React.FC = () => {
	const [selectedAudit, setSelectedAudit] = useState<IAudit | undefined>(
		undefined
	);
	const [searchInput, setSearchInput] = useState('');
	const [excludedActions, setExcludedActions] = useState<string[] | undefined>(
		DefaultExcludedActions
	);
	const [timePeriod, setTimePeriod] = useState(AuditsTimePeriod.Day);
	const debouncedInput = useDebounce(searchInput, 400);

	const { data, isLoading } = useAudits(
		debouncedInput,
		timePeriod,
		excludedActions
	);
	const audits = data || [];

	const handleSearch = useCallback(
		(model: GridFilterModel) => {
			const input = model.quickFilterValues?.[0] as string;
			setSearchInput(input);
		},
		[setSearchInput]
	);

	const selectTimePeriod = React.useMemo(
		() => (
			<AuditSelectTimePeriod
				value={timePeriod}
				excludedActions={excludedActions}
				onValueChange={(newValue) => setTimePeriod(newValue)}
				onExcludeChange={(newExcludedActions) =>
					setExcludedActions(newExcludedActions)
				}
			/>
		),
		[timePeriod, excludedActions]
	);
	const onAuditRowClicked = useCallback(
		(params: GridRowParams) => {
			setSelectedAudit(params.row);
		},
		[setSelectedAudit]
	);

	const handleCloseDrawer = React.useCallback(() => {
		setSelectedAudit(undefined);
	}, [setSelectedAudit]);

	const columns = React.useMemo(() => getAuditsColumns(), []);

	return (
		<>
			<AuditsDataGrid
				loading={isLoading}
				rows={audits?.map((a, i) => ({ ...a, id: i.toString() }))}
				columnVisibilityModel={{}}
				columns={columns}
				checkboxSelection={false}
				// Without this hack - tests that renders this component get 'Maximum call stack size exceeded'
				// We don't know why, but this solves it :/
				autoHeight={IS_TEST_MODE}
				noDataMessageId="grid.data.no.data.audits"
				filterMode="server"
				onFilterModelChange={handleSearch}
				onRowClick={onAuditRowClicked}
				getRowClassName={() => 'audits-table-row'}
				leftActions={selectTimePeriod}
				footer={
					audits?.length > maxAuditsSearch() &&
					intl.tr('audits.max.results', {
						maxAudits: intl.formatNumber(maxAuditsSearch())
					})
				}
			/>
			<AuditDrawer audit={selectedAudit} onClose={handleCloseDrawer} />
		</>
	);
};

export default AuditsTable;
